import { useEffect, useRef, useCallback, useState } from "react";
import { compose } from "redux";
import dayjs from "dayjs";
import {
  Button,
  Card,
  DatePicker,
  Select,
  Tag,
  Modal,
  Input,
  Row,
  Col,
  Typography,
  Space,
  Tooltip,
  Spin,
  AutoComplete,
  Alert,
  Divider,
  Form,
  Table as AntTable,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  CheckOutlined,
  SyncOutlined,
  TagOutlined,
} from "@ant-design/icons";
import _ from "lodash";

import { applyColor } from "utils/apply-color";
import WithHOC from "./actions";
import CoreRenderFunction from "DisplayCore";
import TechBasePageTitle from "DisplayCore/PageTitle";
import { WalletInjectionQueueLayout, queueTableColumns } from "PageLayout/Wallet/WalletInjectionQueue";

const { Text } = Typography;
const { TextArea } = Input;

const QUICK_REASONS = [
  "Duplicate entry",
  "Test data",
  "Incorrect amount",
  "Member request",
];

const WalletInjectionQueue = (props) => {
  const {
    // Trigger
    triggerMonth,
    selectedPrograms,
    triggerProgramOptions,
    triggerResult,
    triggerLoading,
    // Queue list
    filterQuery,
    entries,
    totalCount,
    notReadyCount,
    totalAmount,
    selectedIds,
    listLoading,
    filterProgramOptions = [],
    // Bulk actions
    deleteModalVisible,
    deleteReason,
    deleteAllModalVisible,
    deleteAllReason,
    bulkActionLoading,
    // Tax
    taxMonth,
    memberSearchQuery,
    memberSearchResults,
    memberSearchLoading,
    selectedMembers,
    taxResults,
    taxLoading,
    // Actions
    onChangeHOC,
    triggerSync,
    getQueueList,
    markAsReady,
    markAllAsReady,
    tagBackOrder,
    tagAllBackOrder,
    softDelete,
    deleteAll,
    searchMembers,
    addMember,
    removeMember,
    restoreDefaultTaxMembers,
    applyTaxDeduction,
    colors,
    modulePermissions = {},
  } = props;

  const { can_read = true, can_create = true, can_update = true } = modulePermissions;

  const [markAllReadyModalVisible, setMarkAllReadyModalVisible] = useState(false);
  const [tagAllBackOrderModalVisible, setTagAllBackOrderModalVisible] = useState(false);
  const [filterForm] = Form.useForm();
  const previousMonth = dayjs().subtract(1, "month").format("YYYYMM");
  const currentMonth = dayjs().format("YYYYMM");
  const canMarkReadyForSelectedMonth = [previousMonth, currentMonth].includes(filterQuery.month);

  const applyColFilter = (field, value) =>
    onChangeHOC("filterQuery", { ...filterQuery, [field]: value || null, page: 1 });

  useEffect(() => {
    getQueueList();
  }, [filterQuery]); // eslint-disable-line

  useEffect(() => {
    filterForm.setFieldsValue({
      month: filterQuery.month ? dayjs(filterQuery.month, "YYYYMM") : null,
      transfer_type: filterQuery.transfer_type,
      is_ready: filterQuery.is_ready,
      status: filterQuery.status,
      program_id: filterQuery.program_id,
    });
  }, [filterQuery.month, filterQuery.transfer_type, filterQuery.is_ready, filterQuery.status, filterQuery.program_id]); // eslint-disable-line

  // ─── Debounced member search ───────────────────────────────────────────────

  const debouncedSearch = useRef(
    _.debounce((query) => searchMembers(query), 300)
  ).current;

  const handleMemberSearch = useCallback(
    (val) => {
      onChangeHOC("memberSearchQuery", val);
      debouncedSearch(val);
    },
    [debouncedSearch, onChangeHOC]
  );

  // ─── DSL handlers ──────────────────────────────────────────────────────────

  const onClickAction = (actionKey, ...args) => {
    switch (actionKey) {
      case "UPDATE_TABLE_INFO":
        switch (args[0]) {
          case "page":
            onChangeHOC("filterQuery", { ...filterQuery, page: args[1] });
            break;
          case "pageSize":
            onChangeHOC("filterQuery", { ...filterQuery, pageSize: args[1], page: 1 });
            break;
          default:
        }
        break;
      case "ON_FILTER_CHANGE":
        filterForm.submit();
        break;
      case "RESET_FILTER":
        filterForm.resetFields();
        onChangeHOC("filterQuery", {
          ...filterQuery,
          month: null,
          transfer_type: null,
          is_ready: null,
          is_back_order: null,
          status: null,
          program_id: null,
          member_id: null,
          from_member_id: null,
          remarks: null,
          page: 1,
        });
        break;
      default:
    }
  };

  const onSubmitFilterForm = (formData) => {
    onChangeHOC("filterQuery", {
      ...filterQuery,
      month: formData.month ? formData.month.format("YYYYMM") : null,
      transfer_type: formData.transfer_type || null,
      is_ready: formData.is_ready !== undefined ? formData.is_ready : null,
      is_back_order: formData.is_back_order !== undefined ? formData.is_back_order : null,
      status: formData.status || null,
      program_id: formData.program_id || null,
      member_id: formData.member_id || null,
      from_member_id: formData.from_member_id || null,
      remarks: formData.remarks || null,
      page: 1,
    });
  };

  // ─── Trigger panel ─────────────────────────────────────────────────────────

  const renderTriggerPanel = () => (
    <Card
      className="mb-4"
      title={
        <div className="d-flex">
          <SyncOutlined style={{ marginRight: 6, paddingTop: 4 }} />
          <span>Sync Rewards to Queue</span>
        </div>
      }
      headStyle={applyColor({ background: "$tertiary", color: "!tertiary" }, colors)}
    >
      <div className="w-100">
        <Row gutter={8} align="bottom" className="mb-3">
          <Col span={6}>
            <Text strong className="d-block mb-1">Month</Text>
            <DatePicker
              picker="month"
              value={triggerMonth ? dayjs(triggerMonth, "YYYY-MM") : null}
              onChange={(val) => onChangeHOC("triggerMonth", val ? val.format("YYYY-MM") : null)}
              format="YYYY-MM"
              allowClear={false}
              size="large"
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={12}>
            <Text strong className="d-block mb-1">Bonus Type</Text>
            <Select
              mode="multiple"
              size="large"
              style={{ width: "100%" }}
              placeholder="All active bonus types"
              value={selectedPrograms}
              onChange={(val) => onChangeHOC("selectedPrograms", val)}
              options={triggerProgramOptions}
              allowClear
            />
          </Col>
          {can_create && (
            <Col>
              <Button
                type="primary"
                size="large"
                icon={<SyncOutlined spin={triggerLoading} />}
                loading={triggerLoading}
                onClick={triggerSync}
              >
                Trigger Sync
              </Button>
            </Col>
          )}
        </Row>
        {triggerResult && (
          <div className="mt-3">
            <Divider style={{ margin: "12px 0" }} />
            <Text strong>Sync Results:</Text>
            <div className="mt-2">
              {triggerResult.results?.map((r) => (
                <Row key={r.program_id} className="mb-1" align="middle" gutter={8}>
                  <Col>
                    {r.success ? (
                      <CheckCircleOutlined style={{ color: "#52c41a" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "#ff4d4f" }} />
                    )}
                  </Col>
                  <Col flex="auto">
                    <Text strong>{r.program_name}</Text>
                    {r.success ? (
                      <Text type="secondary">
                        {" "}→ {r.already_queued?.toLocaleString()} / {r.total_in_rewards?.toLocaleString()} in queue (BatchID: {r.reward_batch_id && r.reward_batch_id !== r.batch_id ? `${r.reward_batch_id} → ${r.batch_id}` : (r.batch_id ?? "—")})
                        {r.synced_count > 0 && <>, <Text style={{ color: "#52c41a" }}>+{r.synced_count?.toLocaleString()} new</Text></>}
                        {r.removed_count > 0 && <>, <Text style={{ color: "#ff4d4f" }}>-{r.removed_count?.toLocaleString()} removed</Text></>}
                      </Text>
                    ) : (
                      <Text type="danger"> — {r.error}</Text>
                    )}
                  </Col>
                </Row>
              ))}
              <Divider style={{ margin: "8px 0" }} />
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  // ─── Bulk actions ──────────────────────────────────────────────────────────

  const renderBulkActions = () => {
    const notReadySelected = entries
      .filter((e) => selectedIds.includes(e.id) && !e.is_ready)
      .map((e) => e.id);

    // Tagging is only accepted before a row is marked ready, and only for rewards —
    // those are the rows that build a self-bill and therefore carry a commission_range.
    const taggableSelected = entries.filter(
      (e) => selectedIds.includes(e.id) && !e.is_ready && e.transfer_type === "reward"
    );
    const untaggedSelected = taggableSelected.filter((e) => !e.is_back_order).map((e) => e.id);
    const taggedSelected = taggableSelected.filter((e) => e.is_back_order).map((e) => e.id);

    if (selectedIds.length === 0 && notReadyCount === 0 && totalCount === 0) return null;

    return (
      <Space className="mb-3" wrap>
        {selectedIds.length > 0 && (
          <Text type="secondary">{selectedIds.length} selected</Text>
        )}
        {can_update && untaggedSelected.length > 0 && (
          <Button
            icon={<TagOutlined />}
            loading={bulkActionLoading}
            onClick={() => tagBackOrder(untaggedSelected, true)}
          >
            Tag as Back Order ({untaggedSelected.length})
          </Button>
        )}
        {can_update && taggedSelected.length > 0 && (
          <Button
            icon={<TagOutlined />}
            loading={bulkActionLoading}
            onClick={() => tagBackOrder(taggedSelected, false)}
          >
            Untag Back Order ({taggedSelected.length})
          </Button>
        )}
        {can_update && filterQuery.month && totalCount > 0 && (
          <Button
            icon={<TagOutlined />}
            loading={bulkActionLoading}
            onClick={() => setTagAllBackOrderModalVisible(true)}
          >
            Tag All as Back Order
          </Button>
        )}
        {can_update && canMarkReadyForSelectedMonth && notReadySelected.length > 0 && (
          <Button
            icon={<CheckOutlined />}
            loading={bulkActionLoading}
            onClick={() => markAsReady(notReadySelected)}
          >
            Mark as Ready ({notReadySelected.length})
          </Button>
        )}
        {can_update && selectedIds.length > 0 && (
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={bulkActionLoading}
            onClick={() => onChangeHOC("deleteModalVisible", true)}
          >
            Delete ({selectedIds.length})
          </Button>
        )}
        {can_update && canMarkReadyForSelectedMonth && notReadyCount > 0 && (
          <Button
            icon={<CheckCircleOutlined />}
            loading={bulkActionLoading}
            onClick={() => setMarkAllReadyModalVisible(true)}
          >
            Mark All as Ready ({notReadyCount.toLocaleString()})
          </Button>
        )}
        {can_update && totalCount > 0 && (
          <Button
            danger
            icon={<DeleteOutlined />}
            loading={bulkActionLoading}
            onClick={() => onChangeHOC("deleteAllModalVisible", true)}
          >
            Delete All ({totalCount.toLocaleString()})
          </Button>
        )}
      </Space>
    );
  };

  // ─── Soft-delete modal ─────────────────────────────────────────────────────

  const renderDeleteModal = () => (
    <Modal
      title="Delete Queue Entries"
      open={deleteModalVisible}
      onCancel={() => {
        onChangeHOC("deleteModalVisible", false);
        onChangeHOC("deleteReason", "");
      }}
      footer={
        <Space style={{ justifyContent: "flex-end", width: "100%", display: "flex" }}>
          <Button
            key="cancel"
            onClick={() => {
              onChangeHOC("deleteModalVisible", false);
              onChangeHOC("deleteReason", "");
            }}
          >
            Cancel
          </Button>
          <Button
            key="confirm"
            danger
            type="primary"
            disabled={!deleteReason.trim()}
            loading={bulkActionLoading}
            onClick={() => softDelete(selectedIds, deleteReason)}
          >
            Confirm Delete
          </Button>
        </Space>
      }
    >
      <Text>
        You are about to delete <strong>{selectedIds.length}</strong> entry(ies). All statuses including PROCESSING and COMPLETED will be deleted.
      </Text>
      <div className="mt-3">
        <Text strong>Quick select reason:</Text>
        <div className="mt-2 mb-3">
          {QUICK_REASONS.map((r) => (
            <Tag
              key={r}
              style={{ cursor: "pointer", marginBottom: 6 }}
              color={deleteReason === r ? "blue" : "default"}
              onClick={() => onChangeHOC("deleteReason", r)}
            >
              {r}
            </Tag>
          ))}
        </div>
        <TextArea
          rows={3}
          maxLength={255}
          showCount
          placeholder="Enter deletion reason (required)"
          value={deleteReason}
          onChange={(e) => onChangeHOC("deleteReason", e.target.value)}
        />
      </div>
    </Modal>
  );

  // ─── Tax deduction section ─────────────────────────────────────────────────

  const taxResultColumns = [
    { title: "Member ID", dataIndex: "member_id", key: "member_id" },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "—"),
    },
    {
      title: "Tax Amount (2%)",
      dataIndex: "amount",
      key: "amount",
      align: "right",
      render: (val) => (val != null ? Number(val).toFixed(2) : "—"),
    },
    {
      title: "Status",
      dataIndex: "success",
      key: "success",
      render: (val, row) =>
        val ? (
          <Tag color="success">Success</Tag>
        ) : (
          <Tooltip title={row.error}>
            <Tag color="error">Failed</Tag>
          </Tooltip>
        ),
    },
  ];

  const selectedMemberColumns = [
    { title: "Member ID", dataIndex: "member_id", key: "member_id" },
    {
      title: "Full Name",
      dataIndex: "fullname",
      key: "fullname",
      render: (val) => val || "-",
    },
    {
      title: "",
      key: "remove",
      render: (_, row) => (
        <Button size="small" danger onClick={() => removeMember(row.member_id)}>
          Remove
        </Button>
      ),
    },
  ];

  const renderTaxSection = () => (
    <Card title="Tax Deduction">
      <Row gutter={16} align="middle" className="mb-4">
        <Col>
          <Text strong style={{ marginRight: 8 }}>
            Month:
          </Text>
          <DatePicker
            picker="month"
            value={taxMonth ? dayjs(taxMonth, "YYYY-MM") : null}
            onChange={(val) => onChangeHOC("taxMonth", val ? val.format("YYYY-MM") : null)}
            format="YYYY-MM"
            allowClear={false}
          />
        </Col>
      </Row>

      <Text strong>Search Members:</Text>
      <div className="mt-2 mb-4" style={{ maxWidth: 480 }}>
        <AutoComplete
          style={{ width: "100%" }}
          value={memberSearchQuery}
          onSearch={handleMemberSearch}
          placeholder="Search by member ID or full name"
          notFoundContent={memberSearchLoading ? <Spin size="small" /> : null}
          options={memberSearchResults.map((m) => ({
            value: m.member_id,
            label: (
              <div className="d-flex justify-content-between">
                <Text code>{m.member_id}</Text>
                <Text type="secondary">{m.fullname}</Text>
              </div>
            ),
            member: m,
          }))}
          onSelect={(_, option) => {
            addMember(option.member);
            onChangeHOC("memberSearchQuery", "");
            onChangeHOC("memberSearchResults", []);
          }}
        />
      </div>

      {selectedMembers.length > 0 && (
        <>
          <Text strong>Selected Members ({selectedMembers.length}):</Text>
          <AntTable
            className="mt-2 mb-4"
            size="small"
            rowKey="member_id"
            columns={selectedMemberColumns}
            dataSource={selectedMembers}
            pagination={false}
          />
        </>
      )}

      <Space className="mt-2" wrap>
        {selectedMembers.length > 0 && (
          <Button
            type="primary"
            loading={taxLoading}
            disabled={selectedMembers.length === 0}
            onClick={applyTaxDeduction}
          >
            Apply Tax Deduction
          </Button>
        )}
        <Button onClick={restoreDefaultTaxMembers}>
          Restore Default Members
        </Button>
      </Space>

      {taxResults && (
        <>
          <Divider />
          <Text strong>Tax Deduction Results:</Text>
          {taxResults.some((r) => !r.success) && (
            <Alert
              type="warning"
              className="mt-2 mb-2"
              message="Some members failed. See results below."
            />
          )}
          <AntTable
            className="mt-2"
            size="small"
            rowKey="member_id"
            columns={taxResultColumns}
            dataSource={taxResults}
            pagination={false}
          />
        </>
      )}
    </Card>
  );

  // ─── Queue pagination ───────────────────────────────────────────────────────

  const renderQueuePagination = () => {
    const pageCount = Math.ceil(totalCount / filterQuery.pageSize);
    if (pageCount <= 1 && filterQuery.pageSize <= 10) return null;

    const canPrev = filterQuery.page > 1;
    const canNext = filterQuery.page < pageCount;
    const btnStyle = applyColor({ backgroundColor: "$primary", borderColor: "$primary", color: "#ffffff" }, colors);
    const disabledStyle = { ...btnStyle, opacity: 0.5 };

    return (
      <div style={{ display: "flex", gap: 5, alignItems: "center", justifyContent: "center", margin: "30px 0" }}>
        <Button
          className="custom-color"
          style={canPrev ? btnStyle : disabledStyle}
          disabled={!canPrev}
          onClick={() => onChangeHOC("filterQuery", { ...filterQuery, page: 1 })}
        >{"<<"}</Button>
        <Button
          className="custom-color"
          style={canPrev ? btnStyle : disabledStyle}
          disabled={!canPrev}
          onClick={() => onChangeHOC("filterQuery", { ...filterQuery, page: filterQuery.page - 1 })}
        >{"<"}</Button>
        <div className="ml-3 mr-2">
          Page <strong>{filterQuery.page} of {pageCount}</strong>
        </div>
        <Input
          type="number"
          min={1}
          max={pageCount}
          style={{ width: 70 }}
          value={filterQuery.page}
          onChange={(e) => {
            const val = Number(e.target.value);
            if (val >= 1 && val <= pageCount) {
              onChangeHOC("filterQuery", { ...filterQuery, page: val });
            }
          }}
        />
        <Select
          value={filterQuery.pageSize}
          style={{ width: 120 }}
          onChange={(val) => onChangeHOC("filterQuery", { ...filterQuery, pageSize: val, page: 1 })}
          options={[50, 100, 200, 500].map((n) => ({ value: n, label: `Show ${n}` }))}
        />
        <Button
          className="custom-color"
          style={canNext ? btnStyle : disabledStyle}
          disabled={!canNext}
          onClick={() => onChangeHOC("filterQuery", { ...filterQuery, page: filterQuery.page + 1 })}
        >{">"}</Button>
        <Button
          className="custom-color"
          style={canNext ? btnStyle : disabledStyle}
          disabled={!canNext}
          onClick={() => onChangeHOC("filterQuery", { ...filterQuery, page: pageCount })}
        >{">>"}</Button>
      </div>
    );
  };

  // ─── Page ──────────────────────────────────────────────────────────────────

  if (!can_read) return null;

  return (
    <div className="row">
      <div className="col-12">
        <TechBasePageTitle
          item={{
            heading: "Wallet Injection Queue",
            path: [
              { text: "Wallets", disabled: true },
              { text: "Wallet Injection Queue", disabled: true },
            ],
          }}
          {...props}
        />
      </div>

      <div className="col-12">
        {renderTriggerPanel()}
      </div>

      <div className="col-12">
        <Card
          title={
            <span>
              Queue Management
              {totalCount > 0 && (
                <Text type="secondary" style={{ color: "#ffffff", fontWeight: "normal", marginLeft: 8, fontSize: 13 }}>
                  ({totalCount.toLocaleString()} entries
                  {totalAmount !== 0 && (
                    <> &mdash; Total Amount: <strong>{Number(totalAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></>
                  )})
                </Text>
              )}
            </span>
          }
          className="mb-4"
          headStyle={applyColor({ background: "$primary", color: "#ffffff" }, colors)}
        >
        {renderBulkActions()}
        <Spin spinning={listLoading}>
          <CoreRenderFunction
            {...props}
            item={WalletInjectionQueueLayout.content}
            onClickAction={onClickAction}
            filterForm={filterForm}
            onSubmitFilterForm={onSubmitFilterForm}
          />
          <AntTable
            rowKey="id"
            size="small"
            scroll={{ x: true }}
            components={{
              header: {
                cell: (cellProps) => (
                  <th
                    {...cellProps}
                    style={{
                      ...cellProps.style,
                      ...applyColor({ backgroundColor: "$primary", color: "#ffffff" }, colors),
                    }}
                  />
                ),
              },
            }}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys: selectedIds,
              onChange: (keys) => onChangeHOC("selectedIds", keys),
            }}
            columns={queueTableColumns(filterQuery, applyColFilter)}
            dataSource={entries}
            pagination={false}
          />
          {renderQueuePagination()}
        </Spin>
      </Card>
      </div>

      <div className="col-12">
        {renderTaxSection()}
      </div>

      {renderDeleteModal()}
      <Modal
        title="Delete All Queue Entries"
        open={deleteAllModalVisible}
        onCancel={() => {
          onChangeHOC("deleteAllModalVisible", false);
          onChangeHOC("deleteAllReason", "");
        }}
        footer={
          <Space style={{ justifyContent: "flex-end", width: "100%", display: "flex" }}>
            <Button
              onClick={() => {
                onChangeHOC("deleteAllModalVisible", false);
                onChangeHOC("deleteAllReason", "");
              }}
            >
              Cancel
            </Button>
            <Button
              danger
              type="primary"
              disabled={!deleteAllReason.trim()}
              loading={bulkActionLoading}
              onClick={() => deleteAll(deleteAllReason)}
            >
              Confirm Delete All
            </Button>
          </Space>
        }
      >
        <Text>
          This will delete <strong>all entries</strong> matching the current filter. All statuses including PROCESSING and COMPLETED will be deleted.
        </Text>
        {filterQuery.month && (
          <div className="mt-2">
            <Text type="secondary">Month: <strong>{filterQuery.month.slice(0, 4)}-{filterQuery.month.slice(4)}</strong></Text>
          </div>
        )}
        {filterQuery.transfer_type && (
          <div>
            <Text type="secondary">Type: <strong>{filterQuery.transfer_type}</strong></Text>
          </div>
        )}
        {filterQuery.program_id && (
          <div>
            <Text type="secondary">Program: <strong>{filterQuery.program_id}</strong></Text>
          </div>
        )}
        <div className="mt-3">
          <Text strong>Quick select reason:</Text>
          <div className="mt-2 mb-3">
            {QUICK_REASONS.map((r) => (
              <Tag
                key={r}
                style={{ cursor: "pointer", marginBottom: 6 }}
                color={deleteAllReason === r ? "blue" : "default"}
                onClick={() => onChangeHOC("deleteAllReason", r)}
              >
                {r}
              </Tag>
            ))}
          </div>
          <TextArea
            rows={3}
            maxLength={255}
            placeholder="Enter reason for deletion..."
            value={deleteAllReason}
            onChange={(e) => onChangeHOC("deleteAllReason", e.target.value)}
          />
        </div>
      </Modal>
      <Modal
        title="Mark All as Ready"
        open={markAllReadyModalVisible && canMarkReadyForSelectedMonth}
        onCancel={() => setMarkAllReadyModalVisible(false)}
        footer={
          <Space style={{ justifyContent: "flex-end", width: "100%", display: "flex" }}>
            <Button onClick={() => setMarkAllReadyModalVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              disabled={!canMarkReadyForSelectedMonth}
              loading={bulkActionLoading}
              onClick={() => {
                setMarkAllReadyModalVisible(false);
                markAllAsReady();
              }}
            >
              Confirm
            </Button>
          </Space>
        }
      >
        <Text>
          This will mark <strong>all not-ready entries</strong> matching the current filter as ready.
          The cron job will begin processing them on its next run.
        </Text>
        {filterQuery.month && (
          <div className="mt-2">
            <Text type="secondary">Month: <strong>{filterQuery.month.slice(0, 4)}-{filterQuery.month.slice(4)}</strong></Text>
          </div>
        )}
        {filterQuery.transfer_type && (
          <div>
            <Text type="secondary">Type: <strong>{filterQuery.transfer_type}</strong></Text>
          </div>
        )}
      </Modal>
      <Modal
        title="Tag All as Back Order"
        open={tagAllBackOrderModalVisible}
        onCancel={() => setTagAllBackOrderModalVisible(false)}
        footer={
          <Space style={{ justifyContent: "flex-end", width: "100%", display: "flex" }}>
            <Button onClick={() => setTagAllBackOrderModalVisible(false)}>Cancel</Button>
            <Button
              type="primary"
              loading={bulkActionLoading}
              onClick={() => {
                setTagAllBackOrderModalVisible(false);
                tagAllBackOrder(true);
              }}
            >
              Confirm
            </Button>
          </Space>
        }
      >
        <Text>
          This will tag <strong>all not-ready reward entries</strong> matching the current filter as back orders.
          They will post to a separate <Text code>{`${filterQuery.month || "YYYYMM"}_backorder`}</Text> self-bill
          instead of the regular monthly one.
        </Text>
        <Alert
          className="mt-3"
          type="warning"
          showIcon
          message="Tag a dealer's back orders together"
          description="If some of a dealer's back orders are left untagged, those go into the regular monthly self-bill — which fails outright once that self-bill has been synced to LHDN."
        />
        <div className="mt-3">
          <Text type="secondary">Month: <strong>{filterQuery.month ? `${filterQuery.month.slice(0, 4)}-${filterQuery.month.slice(4)}` : "—"}</strong></Text>
        </div>
        {filterQuery.program_id && (
          <div>
            <Text type="secondary">Bonus Type: <strong>{filterProgramOptions.find((o) => o.value === filterQuery.program_id)?.label || filterQuery.program_id}</strong></Text>
          </div>
        )}
        {filterQuery.member_id && (
          <div>
            <Text type="secondary">Member ID: <strong>{filterQuery.member_id}</strong></Text>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default compose(WithHOC)(WalletInjectionQueue);
